// src/components/HomePage.jsx
// ภาพรวมทีม — every game in the workbook's All_Scrim log, filtered once, then
// shown as headline numbers, recent form, weekly trend, game situations,
// opponents, FS heroes and the full match list. Every rate carries its sample.
import React, { useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Clock3,
  Flame,
  Gauge,
  Hourglass,
  LayoutDashboard,
  ListFilter,
  RotateCcw,
  Search,
  Shield,
  Swords,
  Table2,
  ChartNoAxesCombined,
  TrendingUp,
  Trophy,
  Upload,
  Users,
  AlertTriangle,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from "lucide-react";
import MinimapGameLink, { gameNumbers, useMinimapIndex } from "./minimap/MinimapGameLink";
import { FUTURE_GRACE_DAYS, playedDates, todayYmd } from "../utils/dashboardDates";
import {
  Badge,
  Button,
  Callout,
  Card,
  Delta,
  EmptyState,
  FormStrip,
  HeroAvatar,
  HeroStack,
  Kpi,
  Legend,
  Page,
  PageHeader,
  ResultBadge,
  SearchInput,
  Section,
  Segmented,
  SideBadge,
  WinLossBar,
  selectStyles,
} from "../ui/kit";
import { mean, median, minutes, pct, ymd, ymdThai } from "../ui/format";
import { colors, series } from "../theme/minimalTheme";
import { BarList, HalfGauge, MiniHistogram, Ring, SparkBars, Sparkline, StreakRuns } from "./charts/MiniCharts";

import { PRIORITY_TEAMS, TEAM_TIER_MAP } from "../utils/teamTiers";

const NO_OPP = "ไม่พบข้อมูล";
const TYPE_COLORS = series.filter((_, i) => i !== 1);

const DURATION_BUCKETS = [
  { label: "<10", max: 10 },
  { label: "10–12", max: 12 },
  { label: "12–14", max: 14 },
  { label: "14–16", max: 16 },
  { label: "16–18", max: 18 },
  { label: "18–20", max: 20 },
  { label: "20+", max: Infinity },
];

// --- helpers ---------------------------------------------------------------
function normalizeWeekValue(w) {
  const s = String(w ?? "").trim();
  if (!s) return "";
  const m = s.match(/^w?\s*(\d+)$/i);
  if (m) return `W${Number(m[1])}`;
  return s;
}
const weekOf = (g) => normalizeWeekValue(g?.week ?? g?.Week ?? "") || "";
const weekNum = (w) => Number(String(w).replace(/^\D+/, "")) || 0;
const sortWeeks = (list) => [...list].sort((a, b) => weekNum(a) - weekNum(b) || String(a).localeCompare(String(b)));

// Prefer the parser's resolved win flag (built from the 0/1 FS-Win column with a
// Match Win fallback — same rule the Team Performance report uses) so the Home
// win rate matches it. Fall back to the raw Match Win cell for legacy rows.
function isFsWin(g) {
  if (g && g.win != null) return Number(g.win) === 1;
  return String(g?.match || g?.["Match Win"] || "").toUpperCase() === "FS";
}
const sideOf = (g) => String(g.side || g.Side || "").trim().toLowerCase();
const timeOf = (g) => {
  const n = Number(g.time);
  return g.time !== "" && Number.isFinite(n) && n > 0 ? n : null;
};
const heroList = (s) =>
  String(s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
const tally = () => ({ n: 0, w: 0 });
const bump = (t, win) => {
  t.n += 1;
  if (win) t.w += 1;
};
const describeGame = (g) =>
  [ymd(g.Date), g.competitor && g.competitor !== NO_OPP ? `vs ${g.competitor}` : "", g.type, g.side, isFsWin(g) ? "ชนะ" : "แพ้", timeOf(g) ? `${minutes(g.time)} นาที` : ""]
    .filter(Boolean)
    .join(" · ");

function shiftDateYmd(yyyymmdd, deltaDays) {
  if (!yyyymmdd || String(yyyymmdd).length !== 8) return "";
  const s = String(yyyymmdd);
  const date = new Date(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)));
  date.setDate(date.getDate() + deltaDays);
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}`;
}

/** Everything the page shows, from one filtered list (chronological: date, then sheet order). */
function buildStats(list) {
  const chrono = list
    .map((g, i) => ({ g, i }))
    .sort((a, b) => String(a.g.Date).localeCompare(String(b.g.Date)) || a.i - b.i)
    .map((x) => x.g);
  const n = chrono.length;
  const wins = chrono.filter(isFsWin).length;

  const sides = { blue: tally(), red: tally() };
  const types = {};
  const ends = {};
  const opps = {};
  const weeks = {};
  const weekType = {};
  const picks = {};
  const bans = {};
  const days = new Set();
  const times = [];
  const winTimes = [];
  const lossTimes = [];
  const buckets = DURATION_BUCKETS.map(() => tally());
  const split = { short: tally(), long: tally() };
  let gamesWithPicks = 0;
  let gamesWithBans = 0;

  chrono.forEach((g) => {
    const win = isFsWin(g);
    const s = sideOf(g);
    if (sides[s]) bump(sides[s], win);
    const type = g.type || "ไม่พบข้อมูล";
    bump((types[type] = types[type] || tally()), win);
    const end = g.endType || "ไม่ระบุ";
    bump((ends[end] = ends[end] || tally()), win);
    const opp = g.competitor || NO_OPP;
    const o = (opps[opp] = opps[opp] || { ...tally(), last: "", results: [] });
    bump(o, win);
    o.last = String(g.Date);
    o.results.push({ win, title: describeGame(g) });
    const wk = weekOf(g);
    if (wk) {
      bump((weeks[wk] = weeks[wk] || tally()), win);
      const wt = (weekType[wk] = weekType[wk] || {});
      bump((wt[type] = wt[type] || tally()), win);
    }
    days.add(String(g.Date));
    const t = timeOf(g);
    if (t != null) {
      times.push(t);
      (win ? winTimes : lossTimes).push(t);
      const bi = DURATION_BUCKETS.findIndex((b) => t < b.max);
      bump(buckets[bi], win);
      bump(t < 15 ? split.short : split.long, win);
    }
    const p = heroList(g.fsPick);
    if (p.length) gamesWithPicks += 1;
    p.forEach((h) => bump((picks[h] = picks[h] || tally()), win));
    const b = heroList(g.fsBan);
    if (b.length) gamesWithBans += 1;
    b.forEach((h) => bump((bans[h] = bans[h] || tally()), win));
  });

  // Order-in-time views (streaks, recent form) leave out rows dated far in the future —
  // a mistyped year has no real place on the timeline. Totals above still count them.
  const limit = shiftDateYmd(todayYmd(), FUTURE_GRACE_DAYS);
  const inTime = chrono.filter((g) => String(g.Date) <= limit);
  const timeline = inTime.length ? inTime : chrono;

  // streaks as runs of equal results
  const runs = [];
  timeline.forEach((g) => {
    const win = isFsWin(g);
    const last = runs[runs.length - 1];
    if (last && last.win === win) {
      last.len += 1;
      last.to = g;
    } else runs.push({ win, len: 1, from: g, to: g });
  });
  const longestW = Math.max(0, ...runs.filter((r) => r.win).map((r) => r.len));
  const longestL = Math.max(0, ...runs.filter((r) => !r.win).map((r) => r.len));
  const current = runs[runs.length - 1];

  // last 7 played days (ignoring sheet rows dated far in the future)
  const { latest, futureCount } = playedDates(chrono);
  const from7 = latest ? shiftDateYmd(latest, -6) : "";
  const last7 = latest ? chrono.filter((g) => String(g.Date) >= from7 && String(g.Date) <= latest) : [];
  const days7 = latest
    ? Array.from({ length: 7 }, (_, k) => {
        const date = shiftDateYmd(from7, k);
        const day = last7.filter((g) => String(g.Date) === date);
        return { date, n: day.length, w: day.filter(isFsWin).length };
      })
    : [];

  return {
    chrono,
    n,
    wins,
    losses: n - wins,
    sides,
    types,
    ends,
    opps,
    weeks,
    weekType,
    picks,
    bans,
    gamesWithPicks,
    gamesWithBans,
    days: days.size,
    times,
    avgTime: mean(times),
    medTime: median(times),
    avgWin: mean(winTimes),
    avgLoss: mean(lossTimes),
    minTime: times.length ? Math.min(...times) : null,
    maxTime: times.length ? Math.max(...times) : null,
    buckets,
    split,
    streak: current ? { win: current.win, len: current.len } : null,
    runs,
    timeline,
    hiddenFuture: chrono.length - timeline.length,
    longestW,
    longestL,
    days7,
    latest,
    from7,
    futureCount,
    last7: { n: last7.length, w: last7.filter(isFsWin).length },
    first: chrono.length ? String(chrono[0].Date) : "",
  };
}

/** Click-to-sort for a table: returns sorted rows plus th props per key. */
function useSort(rows, initialKey, initialDir = "desc", getters = {}) {
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });
  const sorted = useMemo(() => {
    const get = getters[sort.key] || ((r) => r[sort.key]);
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);
  const th = (key, firstDir = "desc") => ({
    "data-sortable": "true",
    "aria-sort": sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none",
    onClick: () => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: firstDir })),
  });
  const arrow = (key) => (sort.key === key ? (sort.dir === "asc" ? " ↑" : " ↓") : "");
  return { sorted, th, arrow };
}

function RateCell({ w, n, width = 90 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
      <WinLossBar wins={w} losses={n - w} style={{ width, flex: "none" }} />
      <span className="tnum ds-td-strong" style={{ minWidth: 48, textAlign: "right", color: "var(--text)" }}>
        {pct(n ? w / n : null)}
      </span>
    </div>
  );
}

/** Ranked rows: label · n · W–L bar · rate. */
function BreakdownRows({ rows, total, emptyText = "ไม่มีข้อมูล", showShare = true }) {
  if (!rows.length) return <div className="ds-muted" style={{ fontSize: 13 }}>{emptyText}</div>;
  return (
    <div className="ds-rows">
      {rows.map((r) => (
        <div key={r.label} className="ds-row" style={{ gridTemplateColumns: "minmax(0,1fr) auto" }} title={`${r.label}: ${r.n} เกม · ชนะ ${r.w} แพ้ ${r.n - r.w}`}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 5 }}>
              <span className="ds-truncate" style={{ color: "var(--text)", fontWeight: 500 }}>
                {r.label}
              </span>
              <span className="tnum ds-muted" style={{ fontSize: 12, flex: "none" }}>
                {r.n} เกม{showShare && total ? ` · ${pct(r.n / total, 0)}` : ""} · {r.w}–{r.n - r.w}
              </span>
            </div>
            <WinLossBar wins={r.w} losses={r.n - r.w} />
          </div>
          <div className="tnum" style={{ width: 56, textAlign: "right", fontWeight: 600, color: "var(--text)" }}>
            {pct(r.n ? r.w / r.n : null, 0)}
          </div>
        </div>
      ))}
    </div>
  );
}

const PAGE_SIZES = [15, 30, 50, 100];

export default function HomePage({ games, comboStats, dropdownDates, sourceName }) {
  const navigate = useNavigate();

  // Filter state
  const [result, setResult] = useState("all"); // all | Win | Lose
  const [side, setSide] = useState("all"); // all | blue | red
  const [search, setSearch] = useState("");
  const [competitors, setCompetitors] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [filterMode, setFilterMode] = useState("date");
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [selectedWeekRange, setSelectedWeekRange] = useState({ from: "", to: "" });
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [heroView, setHeroView] = useState("picks");

  const validGames = useMemo(() => (games || []).filter((g) => g.Date && String(g.Date).length === 8), [games]);

  // Minimap Lab link per game row: its number in the day's series (sheet order) and what the Lab already has.
  const gameNo = useMemo(() => gameNumbers(validGames), [validGames]);
  const minimapIndex = useMinimapIndex();

  const weekLabels = useMemo(() => sortWeeks(new Set(validGames.map(weekOf).filter(Boolean))), [validGames]);
  const weekOptions = weekLabels.map((w) => ({ value: w, label: w }));
  const dateOptions = useMemo(() => {
    const source = dropdownDates?.length > 0 ? dropdownDates : [...new Set(validGames.map((g) => g.Date).filter(Boolean))].sort();
    return source.map((d) => ({ value: String(d).replace(".0", ""), label: ymd(d, { long: true }) }));
  }, [dropdownDates, validGames]);

  // Types and opponents ordered by how often they appear (colour follows the type, not the filter).
  const typeOrder = useMemo(() => {
    const c = {};
    validGames.forEach((g) => {
      const t = g.type;
      if (t && t !== "ไม่พบข้อมูล") c[t] = (c[t] || 0) + 1;
    });
    return Object.entries(c).sort((a, b) => b[1] - a[1]).map(([t]) => t);
  }, [validGames]);
  // Orange is the "all games" line, so types take the other categorical slots.
  const typeColor = (t) => (typeOrder.includes(t) ? TYPE_COLORS[typeOrder.indexOf(t) % TYPE_COLORS.length] : colors.faint);
  const allCompetitors = useMemo(() => {
    const c = {};
    validGames.forEach((g) => {
      if (g.competitor) c[g.competitor] = (c[g.competitor] || 0) + 1;
    });
    return Object.entries(c).sort((a, b) => b[1] - a[1]).map(([name, n]) => ({ value: name, label: `${name} (${n})` }));
  }, [validGames]);

  // One filter for the whole page (table, numbers and charts agree).
  const filtered = useMemo(() => {
    let arr = validGames;
    if (result !== "all") arr = arr.filter((g) => isFsWin(g) === (result === "Win"));
    if (side !== "all") arr = arr.filter((g) => sideOf(g) === side);
    if (competitors.length) {
      const sel = new Set(competitors.map((x) => x.value));
      arr = arr.filter((g) => sel.has(g.competitor));
    }
    if (selectedTypes.length) {
      const sel = new Set(selectedTypes.map((x) => x.value));
      arr = arr.filter((g) => sel.has(g.type));
    }
    if (filterMode === "week") {
      if (selectedWeeks.length) {
        arr = arr.filter((g) => selectedWeeks.includes(weekOf(g)));
      } else if (selectedWeekRange.from && selectedWeekRange.to) {
        const f = weekLabels.indexOf(selectedWeekRange.from);
        const t = weekLabels.indexOf(selectedWeekRange.to);
        if (f !== -1 && t !== -1) {
          const sl = weekLabels.slice(Math.min(f, t), Math.max(f, t) + 1);
          arr = arr.filter((g) => sl.includes(weekOf(g)));
        }
      }
    } else {
      if (selectedDates.length) arr = arr.filter((g) => selectedDates.includes(String(g.Date)));
      if (selectedDateRange.from) arr = arr.filter((g) => String(g.Date) >= selectedDateRange.from);
      if (selectedDateRange.to) arr = arr.filter((g) => String(g.Date) <= selectedDateRange.to);
    }
    const txt = search.trim().toLowerCase();
    if (txt) {
      arr = arr.filter(
        (g) =>
          String(g.competitor || "").toLowerCase().includes(txt) ||
          String(g.type || "").toLowerCase().includes(txt) ||
          String(g.endType || "").toLowerCase().includes(txt) ||
          ymd(g.Date).includes(txt) ||
          String(g.fsPick || "").toLowerCase().includes(txt) ||
          String(g.fsBan || "").toLowerCase().includes(txt)
      );
    }
    return arr;
  }, [validGames, result, side, competitors, selectedTypes, filterMode, selectedWeeks, selectedWeekRange, selectedDates, selectedDateRange, search, weekLabels]);

  const stats = useMemo(() => buildStats(filtered), [filtered]);
  const totalStats = useMemo(() => buildStats(validGames), [validGames]);

  const activeFilters =
    (result !== "all") +
    (side !== "all") +
    (competitors.length > 0) +
    (selectedTypes.length > 0) +
    (selectedWeeks.length > 0 || selectedDates.length > 0) +
    Boolean(selectedWeekRange.from || selectedWeekRange.to || selectedDateRange.from || selectedDateRange.to) +
    Boolean(search.trim());

  const clearAll = () => {
    setResult("all");
    setSide("all");
    setSearch("");
    setCompetitors([]);
    setSelectedTypes([]);
    setSelectedWeeks([]);
    setSelectedWeekRange({ from: "", to: "" });
    setSelectedDates([]);
    setSelectedDateRange({ from: "", to: "" });
  };

  // Reset pagination on filter changes
  React.useEffect(() => setPage(1), [filtered, pageSize]);

  // ---------- opponents ----------
  const opponentRows = useMemo(
    () =>
      Object.entries(stats.opps).map(([name, o]) => ({
        name,
        n: o.n,
        w: o.w,
        l: o.n - o.w,
        rate: o.n ? o.w / o.n : 0,
        last: o.last,
        results: o.results,
        tier: TEAM_TIER_MAP[name] ?? null,
        priority: PRIORITY_TEAMS.indexOf(name) === -1 ? 99 : PRIORITY_TEAMS.indexOf(name),
      })),
    [stats.opps]
  );
  const oppSort = useSort(opponentRows, "n", "desc", {
    tier: (r) => (r.tier == null ? 9 : r.tier) * 100 + r.priority,
    name: (r) => r.name,
  });

  // ---------- heroes ----------
  const heroRows = useMemo(() => {
    const src = heroView === "picks" ? stats.picks : stats.bans;
    const base = heroView === "picks" ? stats.gamesWithPicks : stats.gamesWithBans;
    return Object.entries(src)
      .map(([name, t]) => ({ name, n: t.n, w: t.w, share: base ? t.n / base : 0 }))
      .sort((a, b) => b.n - a.n || b.w - a.w)
      .slice(0, 24);
  }, [heroView, stats]);

  // ---------- matches table ----------
  const matchRows = useMemo(() => {
    const idx = new Map(validGames.map((g, i) => [g, i]));
    return filtered.map((g) => ({ g, i: idx.get(g) }));
  }, [filtered, validGames]);
  const matchSort = useSort(matchRows, "date", "desc", {
    date: (r) => String(r.g.Date) + String(100000 + r.i).padStart(6, "0"),
    opp: (r) => r.g.competitor || "",
    type: (r) => r.g.type || "",
    side: (r) => sideOf(r.g),
    result: (r) => (isFsWin(r.g) ? 1 : 0),
    time: (r) => timeOf(r.g),
    end: (r) => r.g.endType || "",
    week: (r) => weekNum(weekOf(r.g)),
  });
  const futureLimit = useMemo(() => shiftDateYmd(todayYmd(), FUTURE_GRACE_DAYS), []);
  const pageCount = Math.max(1, Math.ceil(matchSort.sorted.length / pageSize));
  const pageRows = matchSort.sorted.slice((page - 1) * pageSize, page * pageSize);

  // ---------- charts ----------
  const chartWeeks = useMemo(() => sortWeeks(Object.keys(stats.weeks)), [stats.weeks]);
  const trendTypes = useMemo(
    () => typeOrder.filter((t) => chartWeeks.some((w) => stats.weekType[w]?.[t]?.n)).slice(0, 5),
    [typeOrder, chartWeeks, stats.weekType]
  );

  const trendData = {
    labels: chartWeeks,
    datasets: [
      {
        label: "รวมทุกประเภท",
        data: chartWeeks.map((w) => (stats.weeks[w].n ? +((stats.weeks[w].w / stats.weeks[w].n) * 100).toFixed(1) : null)),
        borderColor: colors.accent,
        backgroundColor: colors.accent,
        borderWidth: 2.5,
        pointRadius: 3.5,
        pointHoverRadius: 6,
        tension: 0.25,
        meta: chartWeeks.map((w) => stats.weeks[w]),
      },
      ...trendTypes.map((t) => ({
        label: t,
        data: chartWeeks.map((w) => {
          const c = stats.weekType[w]?.[t];
          return c?.n ? +((c.w / c.n) * 100).toFixed(1) : null;
        }),
        borderColor: typeColor(t),
        backgroundColor: typeColor(t),
        borderWidth: 1.5,
        pointRadius: 2.5,
        pointHoverRadius: 5,
        tension: 0.25,
        spanGaps: true,
        meta: chartWeeks.map((w) => stats.weekType[w]?.[t] || null),
      })),
    ],
  };
  const pctAxis = {
    min: 0,
    max: 100,
    ticks: { stepSize: 25, callback: (v) => `${v}%`, color: colors.faint },
    grid: { color: colors.grid },
    border: { display: false },
  };
  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      datalabels: { display: false },
      legend: { position: "bottom", labels: { padding: 14 } },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const m = ctx.dataset.meta?.[ctx.dataIndex];
            if (ctx.raw == null || !m) return null;
            return ` ${ctx.dataset.label}: ${ctx.raw}%  (${m.w}–${m.n - m.w}, ${m.n} เกม)`;
          },
        },
      },
    },
    scales: { y: pctAxis, x: { grid: { display: false }, ticks: { color: colors.faint } } },
  };

  const stackBar = (labels, cells) => ({
    labels,
    datasets: [
      {
        label: "ชนะ",
        data: cells.map((c) => c.w),
        backgroundColor: colors.win,
        borderRadius: 3,
        borderSkipped: false,
        borderColor: colors.surface,
        borderWidth: { top: 2 },
        maxBarThickness: 34,
      },
      {
        label: "แพ้",
        data: cells.map((c) => c.n - c.w),
        backgroundColor: colors.lose,
        borderRadius: 3,
        borderSkipped: false,
        borderColor: colors.surface,
        borderWidth: { top: 2 },
        maxBarThickness: 34,
      },
    ],
  });
  const stackOptions = (cells, unit = "") => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => `${items[0].label}${unit}`,
          label: (ctx) => ` ${ctx.dataset.label}: ${ctx.raw} เกม`,
          footer: (items) => {
            const c = cells[items[0].dataIndex];
            return c?.n ? `อัตราชนะ ${pct(c.w / c.n)} · รวม ${c.n} เกม` : "";
          },
        },
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: colors.faint, maxRotation: 0, autoSkip: false, font: { size: 10.5 } } },
      y: { stacked: true, beginAtZero: true, grid: { color: colors.grid }, border: { display: false }, ticks: { precision: 0, color: colors.faint } },
    },
  });
  const weekCells = chartWeeks.map((w) => stats.weeks[w]);

  // ---------- empty workbook ----------
  if (!validGames.length) {
    return (
      <Page>
        <PageHeader eyebrow="วิเคราะห์ทีม" icon={LayoutDashboard} title="ภาพรวมทีม" />
        <Card>
          <EmptyState
            icon={Upload}
            title={sourceName ? "ไม่พบเกมในไฟล์นี้" : "ยังไม่มีไฟล์ข้อมูล"}
            action={
              <Button variant="primary" icon={Upload} onClick={() => navigate("/upload")}>
                {sourceName ? "เลือกไฟล์อื่น" : "อัปโหลดไฟล์ .xlsx"}
              </Button>
            }
          >
            {sourceName
              ? "หน้านี้อ่านชีท All_Scrim (หรือชีทสถิติสครีม) — ไฟล์นี้ไม่มีแถวที่มีวันที่และผลแข่ง"
              : "อัปโหลดไฟล์บันทึกของทีม (ชีท All_Scrim) แล้วภาพรวมทั้งหมดจะแสดงที่นี่ (รีเฟรชหน้าแล้วต้องอัปโหลดใหม่)"}
          </EmptyState>
        </Card>
      </Page>
    );
  }

  const fileLabel = sourceName ? String(sourceName).replace(/\.(xlsx|xls)$/i, "") : "ไฟล์ที่อัปโหลด";
  const wr = stats.n ? stats.wins / stats.n : null;
  const form7 = stats.last7.n ? stats.last7.w / stats.last7.n : null;
  const topOpp = [...opponentRows].filter((r) => r.name !== NO_OPP).sort((a, b) => b.n - a.n)[0];
  const sideRate = (s) => (stats.sides[s].n ? stats.sides[s].w / stats.sides[s].n : null);
  const recent = stats.timeline.slice(-40);
  const lastN = (k) => {
    const s = stats.timeline.slice(-k);
    const w = s.filter(isFsWin).length;
    return { n: s.length, w };
  };
  const last10 = lastN(10);
  const last20 = lastN(20);
  const last40 = lastN(40);

  // Small charts for the headline tiles.
  const weekItems = chartWeeks.map((w) => ({ label: w, ...stats.weeks[w] }));
  const dayPoints = stats.days7.map((d) => ({ label: ymdThai(d.date), value: d.n ? d.w / d.n : null, n: d.n, w: d.w }));
  const durBins = (() => {
    const timed = stats.chrono.map((g) => ({ t: timeOf(g), win: isFsWin(g) })).filter((x) => x.t != null);
    if (!timed.length) return [];
    const lo = Math.max(0, Math.floor(Math.min(...timed.map((x) => x.t)) / 2) * 2);
    const top = Math.min(30, Math.ceil(Math.max(...timed.map((x) => x.t)) / 2) * 2);
    const bins = [];
    for (let a = lo; a < Math.max(top, lo + 2); a += 2) bins.push({ from: a, to: a + 2, label: `${a}–${a + 2}`, w: 0, l: 0 });
    timed.forEach(({ t, win }) => {
      const b = bins[Math.min(bins.length - 1, Math.floor((t - lo) / 2))];
      if (win) b.w += 1;
      else b.l += 1;
    });
    const last = bins[bins.length - 1];
    if (timed.some((x) => x.t >= last.to)) last.label = `${last.from}+`;
    return bins;
  })();
  const runsShown = stats.runs.slice(-18).map((r) => ({
    win: r.win,
    len: r.len,
    title: `${r.win ? "ชนะ" : "แพ้"} ${r.len} ติด · ${ymd(r.from.Date)}${r.from.Date !== r.to.Date ? `–${ymd(r.to.Date)}` : ""}`,
  }));
  const topOpps = opponentRows
    .filter((r) => r.name !== NO_OPP)
    .sort((a, b) => b.n - a.n)
    .slice(0, 4)
    .map((r) => ({ label: r.name, n: r.n, w: r.w }));

  // Momentum over the recent games: +1 per win, −1 per loss.
  const momentum = recent.reduce((acc, g) => [...acc, (acc[acc.length - 1] || 0) + (isFsWin(g) ? 1 : -1)], []);
  const momentumData = {
    labels: recent.map((_, i) => i + 1),
    datasets: [
      {
        label: "ชนะ − แพ้ สะสม",
        data: momentum,
        borderColor: colors.ink2,
        borderWidth: 1.5,
        pointRadius: recent.map((_, i) => (i === recent.length - 1 ? 3.5 : 0)),
        pointBackgroundColor: recent.map((g) => (isFsWin(g) ? colors.win : colors.lose)),
        pointHoverRadius: 4,
        tension: 0.25,
        fill: { target: "origin", above: "rgba(47,191,113,0.25)", below: "rgba(239,77,90,0.25)" },
      },
    ],
  };
  const momentumOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      datalabels: { display: false },
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => describeGame(recent[items[0].dataIndex]),
          label: (ctx) => ` สะสม ${ctx.raw > 0 ? "+" : ""}${ctx.raw}`,
        },
      },
    },
    scales: {
      x: { display: false },
      y: { grid: { color: (ctx) => (ctx.tick?.value === 0 ? colors.ink2 : colors.grid) }, border: { display: false }, ticks: { color: colors.faint, precision: 0, maxTicksLimit: 5 } },
    },
  };
  const typeRows = Object.entries(stats.types)
    .map(([label, t]) => ({ label, ...t }))
    .sort((a, b) => b.n - a.n);
  const endRows = Object.entries(stats.ends)
    .map(([label, t]) => ({ label, ...t }))
    .sort((a, b) => b.n - a.n);
  const filteredNote = activeFilters ? `ตามตัวกรอง ${stats.n} จาก ${validGames.length} เกม` : `ทุกเกมในชีท (${validGames.length})`;

  return (
    <Page>
      <PageHeader
        eyebrow="วิเคราะห์ทีม"
        icon={LayoutDashboard}
        title="ภาพรวมทีม"
        subtitle={
          <>
            <span>
              จาก <b style={{ color: "var(--text)" }}>{fileLabel}</b> · ชีท All_Scrim
            </span>
            <span className="ds-muted">•</span>
            <span>
              <b className="tnum" style={{ color: "var(--text)" }}>{validGames.length.toLocaleString("en-US")}</b> เกม ·{" "}
              {ymdThai(totalStats.first)} – {ymdThai(totalStats.latest)}
            </span>
            {totalStats.futureCount > 0 && (
              <Badge tone="warn" title="แถวที่ลงวันที่เกินวันนี้ไปมากกว่า 7 วัน — นับในสถิติ แต่ไม่ใช้เป็นวันล่าสุด">
                <AlertTriangle size={12} /> {totalStats.futureCount} เกมลงวันที่ล่วงหน้าในชีท
              </Badge>
            )}
          </>
        }
        actions={
          <>
            <Button variant="ghost" icon={Table2} onClick={() => navigate("/draft-recap")}>
              Draft Recap
            </Button>
            <Button variant="soft" icon={ChartNoAxesCombined} onClick={() => navigate("/charts")}>
              ห้องกราฟ
            </Button>
            <Button variant="primary" icon={Swords} onClick={() => navigate("/draft")}>
              เริ่มดราฟ
            </Button>
          </>
        }
      />

      {/* Filters — apply to every number, chart and the table below */}
      <div className="ds-toolbar ds-toolbar--sticky" style={{ marginBottom: 18 }}>
        <ListFilter size={16} color="var(--text-3)" />
        <Segmented
          ariaLabel="ผลการแข่งขัน"
          value={result}
          onChange={setResult}
          options={[
            { value: "all", label: "ทุกผล" },
            { value: "Win", label: "ชนะ" },
            { value: "Lose", label: "แพ้" },
          ]}
        />
        <Segmented
          ariaLabel="ฝั่งที่เล่น"
          value={side}
          onChange={setSide}
          options={[
            { value: "all", label: "ทุกฝั่ง" },
            { value: "blue", label: "Blue" },
            { value: "red", label: "Red" },
          ]}
        />
        <div style={{ minWidth: 190, flex: "1 1 190px", maxWidth: 280 }}>
          <ReactSelect
            isMulti
            value={selectedTypes}
            onChange={(v) => setSelectedTypes(v || [])}
            options={typeOrder.map((t) => ({ value: t, label: t }))}
            placeholder="ประเภทการแข่ง"
            styles={selectStyles}
            isClearable
            aria-label="ประเภทการแข่ง"
          />
        </div>
        <div style={{ minWidth: 210, flex: "1 1 210px", maxWidth: 320 }}>
          <ReactSelect
            isMulti
            value={competitors}
            onChange={(v) => setCompetitors(v || [])}
            options={allCompetitors}
            placeholder="คู่แข่ง"
            styles={selectStyles}
            isClearable
            aria-label="คู่แข่ง"
          />
        </div>
        <SearchInput
          value={search}
          onChange={setSearch}
          icon={Search}
          placeholder="ค้นหา ทีม / ฮีโร่ / วันที่"
          style={{ flex: "1 1 180px", maxWidth: 240 }}
          aria-label="ค้นหา"
        />
        <div className="ds-toolbar-sep" />
        <CalendarDays size={16} color="var(--text-3)" />
        <Segmented
          ariaLabel="กรองช่วงเวลา"
          value={filterMode}
          onChange={setFilterMode}
          options={[
            { value: "date", label: "วันที่" },
            { value: "week", label: "สัปดาห์" },
          ]}
        />
        {filterMode === "week" ? (
          <>
            <div style={{ minWidth: 170, flex: "1 1 170px", maxWidth: 260 }}>
              <ReactSelect
                isMulti
                value={weekOptions.filter((w) => selectedWeeks.includes(w.value))}
                onChange={(opts) => {
                  setSelectedWeeks(opts ? opts.map((w) => w.value) : []);
                  setSelectedWeekRange({ from: "", to: "" });
                }}
                options={weekOptions}
                placeholder="เลือกสัปดาห์"
                styles={selectStyles}
                isClearable
              />
            </div>
            <span className="ds-muted" style={{ fontSize: 12 }}>หรือ</span>
            <RangeSelects
              options={weekOptions}
              value={selectedWeekRange}
              onChange={(r) => {
                setSelectedWeekRange(r);
                setSelectedWeeks([]);
              }}
            />
          </>
        ) : (
          <>
            <div style={{ minWidth: 170, flex: "1 1 170px", maxWidth: 260 }}>
              <ReactSelect
                isMulti
                value={dateOptions.filter((d) => selectedDates.includes(d.value))}
                onChange={(opts) => {
                  setSelectedDates(opts ? opts.map((d) => d.value) : []);
                  setSelectedDateRange({ from: "", to: "" });
                }}
                options={dateOptions}
                placeholder="เลือกวันที่"
                styles={selectStyles}
                isClearable
              />
            </div>
            <span className="ds-muted" style={{ fontSize: 12 }}>หรือ</span>
            <RangeSelects
              options={dateOptions}
              value={selectedDateRange}
              onChange={(r) => {
                setSelectedDateRange(r);
                setSelectedDates([]);
              }}
            />
          </>
        )}
        <div className="ds-toolbar-spacer" />
        <span className="ds-muted tnum" style={{ fontSize: 12.5 }}>
          {stats.n.toLocaleString("en-US")} / {validGames.length.toLocaleString("en-US")} เกม
        </span>
        <Button size="sm" variant={activeFilters ? "soft" : "ghost"} icon={RotateCcw} onClick={clearAll} disabled={!activeFilters}>
          ล้างตัวกรอง{activeFilters ? ` (${activeFilters})` : ""}
        </Button>
      </div>

      {!stats.n ? (
        <Card>
          <EmptyState icon={ListFilter} title="ไม่มีเกมที่ตรงกับตัวกรอง" action={<Button onClick={clearAll} icon={RotateCcw}>ล้างตัวกรอง</Button>}>
            ลองเอาตัวกรองบางอย่างออก หรือเปลี่ยนช่วงวันที่
          </EmptyState>
        </Card>
      ) : (
        <>
          {/* Headline numbers — each tile carries its own small chart */}
          <div className="ds-grid ds-grid--kpi4">
            <Kpi
              label="อัตราชนะ"
              icon={Trophy}
              tone="accent"
              value={pct(wr)}
              aside={<Ring wins={stats.wins} losses={stats.losses} size={62} thickness={8} />}
              meter={<WinLossBar wins={stats.wins} losses={stats.losses} />}
              sub={
                <>
                  <span className="ds-win tnum">ชนะ {stats.wins}</span>
                  <span className="ds-loss tnum">แพ้ {stats.losses}</span>
                </>
              }
            />
            <Kpi
              label="จำนวนเกม"
              icon={Gauge}
              value={stats.n.toLocaleString("en-US")}
              unit="เกม"
              chart={weekItems.length ? <SparkBars items={weekItems} height={40} /> : null}
              sub={
                <>
                  {weekItems.length ? `รายสัปดาห์ ${weekItems[0].label}–${weekItems[weekItems.length - 1].label} · ` : ""}
                  {stats.days} วันที่เล่น · {(stats.n / Math.max(1, stats.days)).toFixed(1)} เกม/วัน · {opponentRows.filter((r) => r.name !== NO_OPP).length} คู่แข่ง
                </>
              }
            />
            <Kpi
              label="ฟอร์ม 7 วันล่าสุด"
              icon={Flame}
              tone={form7 == null ? undefined : form7 >= (wr ?? 0) ? "win" : "loss"}
              value={pct(form7)}
              title={stats.latest ? `${ymd(stats.from7)} – ${ymd(stats.latest)}` : undefined}
              chart={dayPoints.length ? <Sparkline points={dayPoints} reference={wr} height={40} /> : null}
              sub={
                <>
                  {form7 != null && wr != null ? <Delta value={(form7 - wr) * 100} suffix=" pp" title="เทียบอัตราชนะรวม (percentage points)" /> : null}
                  <span className="tnum">
                    {stats.last7.w}–{stats.last7.n - stats.last7.w} · {ymd(stats.from7)}–{ymd(stats.latest)} · เส้นประ = เฉลี่ยรวม
                  </span>
                </>
              }
            />
            <Kpi
              label="ฝั่ง Blue"
              icon={Shield}
              tone="blue"
              value={pct(sideRate("blue"))}
              aside={<HalfGauge value={sideRate("blue")} mark={wr} color="var(--blue-side)" n={stats.sides.blue.n} />}
              meter={<WinLossBar wins={stats.sides.blue.w} losses={stats.sides.blue.n - stats.sides.blue.w} />}
              sub={
                <span className="tnum">
                  {stats.sides.blue.w}–{stats.sides.blue.n - stats.sides.blue.w} · {stats.sides.blue.n} เกม ({pct(stats.n ? stats.sides.blue.n / stats.n : null, 0)} ของทั้งหมด)
                </span>
              }
            />
            <Kpi
              label="ฝั่ง Red"
              icon={Shield}
              tone="red"
              value={pct(sideRate("red"))}
              aside={<HalfGauge value={sideRate("red")} mark={wr} color="var(--red-side)" n={stats.sides.red.n} />}
              meter={<WinLossBar wins={stats.sides.red.w} losses={stats.sides.red.n - stats.sides.red.w} />}
              sub={
                <span className="tnum">
                  {stats.sides.red.w}–{stats.sides.red.n - stats.sides.red.w} · {stats.sides.red.n} เกม ({pct(stats.n ? stats.sides.red.n / stats.n : null, 0)} ของทั้งหมด)
                </span>
              }
            />
            <Kpi
              label="เวลาเฉลี่ยต่อเกม"
              icon={Hourglass}
              value={minutes(stats.avgTime)}
              unit="นาที"
              chart={durBins.length ? <MiniHistogram bins={durBins} marker={stats.avgTime} markerLabel={`เฉลี่ย ${minutes(stats.avgTime)} นาที`} height={40} /> : null}
              sub={
                <span className="tnum">
                  ชนะ {minutes(stats.avgWin)} · แพ้ {minutes(stats.avgLoss)} · กลาง {minutes(stats.medTime)}
                  {stats.minTime != null ? ` · สั้นสุด ${minutes(stats.minTime)} ยาวสุด ${minutes(stats.maxTime)}` : ""}
                </span>
              }
            />
            <Kpi
              label="สตรีคตอนนี้"
              icon={TrendingUp}
              tone={stats.streak ? (stats.streak.win ? "win" : "loss") : undefined}
              value={stats.streak ? `${stats.streak.win ? "ชนะ" : "แพ้"} ${stats.streak.len}` : "–"}
              unit={stats.streak ? "ติด" : ""}
              chart={runsShown.length ? <StreakRuns runs={runsShown} height={44} /> : null}
              sub={<span className="tnum">{runsShown.length} สตรีคล่าสุด (บน = ชนะ ล่าง = แพ้) · ยาวสุด ชนะ {stats.longestW} · แพ้ {stats.longestL}</span>}
            />
            <Kpi
              label="คู่ซ้อมบ่อยที่สุด"
              icon={Users}
              value={topOpp ? topOpp.name : "–"}
              chart={topOpps.length ? <BarList rows={topOpps} /> : null}
              sub={topOpp ? <span className="tnum">{topOpp.n} เกม · ชนะ {pct(topOpp.rate)} ({topOpp.w}–{topOpp.l})</span> : null}
            />
          </div>

          {/* Recent form */}
          <Card
            style={{ marginTop: 16 }}
            title="ฟอร์มล่าสุด"
            icon={Flame}
            subtitle={`${recent.length} เกมล่าสุด เรียงจากเก่าไปใหม่ — ชี้ที่ช่องหรือกราฟเพื่อดูรายละเอียดเกม${stats.hiddenFuture ? ` · ไม่รวม ${stats.hiddenFuture} เกมที่ชีทลงวันที่ล่วงหน้า` : ""}`}
          >
            <div className="hp-form-grid">
              <div style={{ display: "grid", gap: 14, alignContent: "start", minWidth: 0 }}>
                <FormStrip results={recent.map((g) => ({ win: isFsWin(g), title: describeGame(g) }))} />
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                  {[
                    ["10 เกม", last10],
                    ["20 เกม", last20],
                    [`${recent.length} เกม`, last40],
                  ].map(([label, t]) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Ring wins={t.w} losses={t.n - t.w} size={52} thickness={7} center={pct(t.n ? t.w / t.n : null, 0)} />
                      <div style={{ lineHeight: 1.3 }}>
                        <div style={{ fontSize: 12, color: "var(--text-3)" }}>{label}ล่าสุด</div>
                        <div className="tnum" style={{ fontWeight: 600 }}>
                          <span className="ds-win">{t.w}</span>–<span className="ds-loss">{t.n - t.w}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>โมเมนตัม (ชนะ − แพ้ สะสม)</span>
                  <span className="ds-muted tnum" style={{ fontSize: 11.5 }}>
                    สุทธิ {momentum.length ? `${momentum[momentum.length - 1] > 0 ? "+" : ""}${momentum[momentum.length - 1]}` : "–"}
                  </span>
                </div>
                <div style={{ height: 120 }}>
                  <Line data={momentumData} options={momentumOptions} />
                </div>
              </div>
            </div>
            <style>{`
              .hp-form-grid { display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr); gap: 24px; align-items: start; }
              @media (max-width: 980px) { .hp-form-grid { grid-template-columns: minmax(0, 1fr); } }
            `}</style>
          </Card>

          {/* Trend */}
          <Section title="แนวโน้มรายสัปดาห์" desc={filteredNote}>
            <div className="ds-grid ds-grid--2">
              <Card title="อัตราชนะรายสัปดาห์" icon={TrendingUp} subtitle="เส้นส้ม = รวมทุกประเภท · เส้นอื่นแยกตามประเภทการแข่ง (ชี้ดู W–L และจำนวนเกม)">
                <div style={{ height: 300 }}>
                  {chartWeeks.length ? <Line data={trendData} options={trendOptions} /> : <EmptyState title="ไม่มีข้อมูลสัปดาห์" />}
                </div>
              </Card>
              <Card
                title="จำนวนเกมรายสัปดาห์"
                icon={CalendarDays}
                subtitle="ชนะ / แพ้ ในแต่ละสัปดาห์"
                actions={<Legend items={[{ label: "ชนะ", color: colors.win }, { label: "แพ้", color: colors.lose }]} />}
              >
                <div style={{ height: 300 }}>
                  {chartWeeks.length ? <Bar data={stackBar(chartWeeks, weekCells)} options={stackOptions(weekCells)} /> : null}
                </div>
              </Card>
            </div>
          </Section>

          {/* Game situations */}
          <Section title="สถานการณ์เกม" desc="ประเภทการแข่ง รูปแบบการจบเกม และความยาวเกม">
            <div className="ds-grid ds-grid--3">
              <Card title="ประเภทการแข่ง" icon={Swords} subtitle="จำนวนเกม · สัดส่วน · W–L · อัตราชนะ">
                <BreakdownRows rows={typeRows} total={stats.n} />
              </Card>
              <Card title="รูปแบบการจบเกม" icon={Flame} subtitle="End Type ตามที่บันทึกในชีท — ผลชนะ/แพ้ เมื่อเกมจบแบบนั้น">
                <BreakdownRows rows={endRows} total={stats.n} />
              </Card>
              <Card
                title="ความยาวเกม"
                icon={Clock3}
                subtitle={`${stats.times.length} เกมที่มีเวลา · สั้นสุด ${minutes(stats.minTime)} · ยาวสุด ${minutes(stats.maxTime)} นาที`}
                actions={<Legend items={[{ label: "ชนะ", color: colors.win }, { label: "แพ้", color: colors.lose }]} />}
              >
                <div style={{ height: 190 }}>
                  <Bar data={stackBar(DURATION_BUCKETS.map((b) => b.label), stats.buckets)} options={stackOptions(stats.buckets, " นาที")} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
                  {[
                    ["ต่ำกว่า 15 นาที", stats.split.short],
                    ["15 นาทีขึ้นไป", stats.split.long],
                  ].map(([label, t]) => (
                    <div key={label} style={{ padding: "10px 12px", borderRadius: 10, background: "var(--bg-elev)", border: "1px solid var(--border)" }}>
                      <div className="ds-muted" style={{ fontSize: 12 }}>{label}</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "3px 0 6px" }}>
                        <span style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700 }}>{pct(t.n ? t.w / t.n : null, 0)}</span>
                        <span className="ds-muted tnum" style={{ fontSize: 12 }}>
                          {t.w}–{t.n - t.w}
                        </span>
                      </div>
                      <WinLossBar wins={t.w} losses={t.n - t.w} height={4} />
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </Section>

          {/* Opponents */}
          <Section title="คู่แข่ง" desc={`${opponentRows.length} ทีม · คลิกหัวคอลัมน์เพื่อเรียง`}>
            <Card noBody>
              <div className="ds-table-wrap" style={{ border: 0, borderRadius: "var(--r-lg)", maxHeight: 520 }}>
                <table className="ds-table">
                  <thead>
                    <tr>
                      <th {...oppSort.th("name", "asc")}>ทีม{oppSort.arrow("name")}</th>
                      <th {...oppSort.th("tier", "asc")}>Tier{oppSort.arrow("tier")}</th>
                      <th className="ds-td-num" {...oppSort.th("n")}>เกม{oppSort.arrow("n")}</th>
                      <th className="ds-td-num" {...oppSort.th("w")}>ชนะ{oppSort.arrow("w")}</th>
                      <th className="ds-td-num" {...oppSort.th("l")}>แพ้{oppSort.arrow("l")}</th>
                      <th className="ds-td-num" {...oppSort.th("rate")}>อัตราชนะ{oppSort.arrow("rate")}</th>
                      <th>5 เกมล่าสุด</th>
                      <th className="ds-td-num" {...oppSort.th("last")}>เจอล่าสุด{oppSort.arrow("last")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oppSort.sorted.map((r) => (
                      <tr key={r.name}>
                        <td className="ds-td-strong">{r.name === NO_OPP ? <span className="ds-muted">ไม่ระบุคู่แข่ง</span> : r.name}</td>
                        <td>{r.tier ? <Badge tone={r.tier === 1 ? "accent" : undefined}>T{r.tier}</Badge> : <span className="ds-muted">–</span>}</td>
                        <td className="ds-td-num ds-td-strong">{r.n}</td>
                        <td className="ds-td-num ds-win">{r.w}</td>
                        <td className="ds-td-num ds-loss">{r.l}</td>
                        <td className="ds-td-num">
                          <RateCell w={r.w} n={r.n} />
                        </td>
                        <td>
                          <FormStrip results={r.results.slice(-5)} />
                        </td>
                        <td className="ds-td-num">{ymd(r.last)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </Section>

          {/* FS heroes */}
          <Section
            title="ฮีโร่ของ FS"
            desc={
              heroView === "picks"
                ? `จาก ${stats.gamesWithPicks} เกมที่บันทึกพิก · % = สัดส่วนเกมที่พิก`
                : `จาก ${stats.gamesWithBans} เกมที่บันทึกแบน · % = สัดส่วนเกมที่แบน`
            }
            actions={
              <Segmented
                ariaLabel="พิกหรือแบน"
                accent
                value={heroView}
                onChange={setHeroView}
                options={[
                  { value: "picks", label: "ที่ FS พิก" },
                  { value: "bans", label: "ที่ FS แบน" },
                ]}
              />
            }
          >
            <Card>
              {heroRows.length ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 8 }}>
                  {heroRows.map((h, i) => (
                    <div
                      key={h.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 11,
                        padding: "8px 10px",
                        borderRadius: 12,
                        background: "var(--bg-elev)",
                        border: "1px solid var(--border)",
                        minWidth: 0,
                      }}
                      title={`${h.name}: ${heroView === "picks" ? "พิก" : "แบน"} ${h.n} เกม · ทีมชนะ ${h.w} แพ้ ${h.n - h.w}`}
                    >
                      <span className="tnum ds-muted" style={{ width: 18, fontSize: 11.5, textAlign: "right", flex: "none" }}>
                        {i + 1}
                      </span>
                      <HeroAvatar name={h.name} size={38} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                          <span className="ds-truncate" style={{ fontWeight: 600, color: "var(--text)", fontSize: 13.5 }}>
                            {h.name}
                          </span>
                          <span className="tnum" style={{ fontWeight: 600, fontSize: 13 }}>
                            {pct(h.n ? h.w / h.n : null, 0)}
                          </span>
                        </div>
                        <div className="ds-muted tnum" style={{ fontSize: 11.5, margin: "1px 0 5px" }}>
                          {h.n} เกม · {pct(h.share, 0)} · {h.w}–{h.n - h.w}
                        </div>
                        <WinLossBar wins={h.w} losses={h.n - h.w} height={4} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={Users} title="ไม่มีข้อมูลฮีโร่">
                  ชีทนี้ไม่มีคอลัมน์ {heroView === "picks" ? "pick" : "ban"} ของ FS ในแถวที่เลือก
                </EmptyState>
              )}
            </Card>
          </Section>

          {/* Match list */}
          <Section
            title="รายการแข่งทั้งหมด"
            desc={`${matchSort.sorted.length.toLocaleString("en-US")} เกม · ปุ่มแผนที่ข้างชื่อทีม เปิด/สร้างเกมนั้นใน Minimap Lab`}
            actions={
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--text-3)" }}>
                แถวต่อหน้า
                <select className="ds-select" style={{ width: 76, height: 30 }} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                  {PAGE_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            }
          >
            <Card noBody>
              <div className="ds-table-wrap" style={{ border: 0, borderRadius: "var(--r-lg) var(--r-lg) 0 0" }}>
                <table className="ds-table" style={{ minWidth: 1080 }}>
                  <thead>
                    <tr>
                      <th {...matchSort.th("date")}>วันที่{matchSort.arrow("date")}</th>
                      <th {...matchSort.th("week", "asc")}>สัปดาห์{matchSort.arrow("week")}</th>
                      <th {...matchSort.th("opp", "asc")}>คู่แข่ง{matchSort.arrow("opp")}</th>
                      <th {...matchSort.th("type", "asc")}>ประเภท{matchSort.arrow("type")}</th>
                      <th {...matchSort.th("side", "asc")}>ฝั่ง{matchSort.arrow("side")}</th>
                      <th {...matchSort.th("result")}>ผล{matchSort.arrow("result")}</th>
                      <th className="ds-td-num" {...matchSort.th("time")}>เวลา{matchSort.arrow("time")}</th>
                      <th {...matchSort.th("end", "asc")}>จบแบบ{matchSort.arrow("end")}</th>
                      <th>FS พิก</th>
                      <th>FS แบน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(({ g, i }) => {
                      const n = gameNo.get(g);
                      const future = String(g.Date) > futureLimit;
                      return (
                        <tr key={`${g.Date}-${i}`}>
                          <td className="ds-td-strong tnum" style={{ whiteSpace: "nowrap" }}>
                            {ymd(g.Date, { long: true })}
                            {future ? (
                              <Badge tone="warn" style={{ marginLeft: 6, height: 18 }} title="วันที่ในชีทเลยวันนี้ไปมาก — น่าจะพิมพ์ปีผิด (ไม่ได้แก้ไฟล์ให้)">
                                ล่วงหน้า
                              </Badge>
                            ) : null}
                          </td>
                          <td>{weekOf(g) || "–"}</td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <span className="ds-td-strong" style={{ color: "var(--text)" }}>
                              {g.competitor && g.competitor !== NO_OPP ? g.competitor : <span className="ds-muted">–</span>}
                            </span>
                            {n ? <span className="ds-muted tnum" style={{ fontSize: 11.5, marginLeft: 6 }}>เกม {n}</span> : null}
                            <MinimapGameLink game={g} n={n} index={minimapIndex} />
                          </td>
                          <td>
                            {g.type && g.type !== "ไม่พบข้อมูล" ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                <i style={{ width: 8, height: 8, borderRadius: 2, background: typeColor(g.type), display: "inline-block" }} />
                                {g.type}
                              </span>
                            ) : (
                              <span className="ds-muted">–</span>
                            )}
                          </td>
                          <td>
                            <SideBadge side={g.side} />
                          </td>
                          <td>
                            <ResultBadge win={isFsWin(g)} />
                          </td>
                          <td className="ds-td-num">{minutes(g.time)}</td>
                          <td>{g.endType || <span className="ds-muted">–</span>}</td>
                          <td>
                            <HeroStack names={heroList(g.fsPick)} size={24} />
                          </td>
                          <td>
                            <HeroStack names={heroList(g.fsBan)} size={20} max={6} dim />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} pageCount={pageCount} setPage={setPage} total={matchSort.sorted.length} pageSize={pageSize} />
            </Card>
          </Section>

          <Callout style={{ marginTop: 18 }}>
            ตัวเลขทั้งหมดในหน้านี้นับจากชีท All_Scrim ของไฟล์ <b>{fileLabel}</b> — ผลชนะใช้คอลัมน์ FS Win (0/1) ก่อน ถ้าว่างใช้ Match Win
            ตรงกับหน้า Team Performance
          </Callout>
        </>
      )}
    </Page>
  );
}

function RangeSelects({ options, value, onChange }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <select className="ds-select" style={{ width: 128 }} value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })} aria-label="จาก">
        <option value="">จาก</option>
        {options.map(({ value: v, label }) => (
          <option key={v} value={v}>
            {label}
          </option>
        ))}
      </select>
      <span className="ds-muted">–</span>
      <select className="ds-select" style={{ width: 128 }} value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })} aria-label="ถึง">
        <option value="">ถึง</option>
        {options.map(({ value: v, label }) => (
          <option key={v} value={v}>
            {label}
          </option>
        ))}
      </select>
    </span>
  );
}

function Pagination({ page, pageCount, setPage, total, pageSize }) {
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(total, page * pageSize);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        padding: "10px 14px",
        borderTop: "1px solid var(--border)",
      }}
    >
      <span className="ds-muted tnum" style={{ fontSize: 12.5 }}>
        แสดง {from}–{to} จาก {total.toLocaleString("en-US")} เกม
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <Button size="sm" variant="ghost" icon={ChevronsLeft} onClick={() => setPage(1)} disabled={page === 1} aria-label="หน้าแรก" />
        <Button size="sm" variant="ghost" icon={ChevronLeft} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} aria-label="หน้าก่อน" />
        <span className="tnum" style={{ fontSize: 12.5, minWidth: 74, textAlign: "center", color: "var(--text-2)" }}>
          หน้า {page} / {pageCount}
        </span>
        <Button size="sm" variant="ghost" icon={ChevronRight} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page === pageCount} aria-label="หน้าถัดไป" />
        <Button size="sm" variant="ghost" icon={ChevronsRight} onClick={() => setPage(pageCount)} disabled={page === pageCount} aria-label="หน้าสุดท้าย" />
      </div>
    </div>
  );
}
